import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Badge, Button, Flex, Image, Input, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { apiClient } from '../../services/apiClient';
import DataTable from '../../components/table/DataTable';
import { FilterSidebar, type FilterSection } from '../../components/FilterSidebar';
import { FilterIcon } from '../../components/DataTable';

interface CuratedPlaylistItem {
  id: string;
  name: string;
  description: string | null;
  thumbnailUrl: string | null;
  trackCount: number;
  isPublished: boolean;
  isLicensed: boolean;
  createdAt: string;
}

interface PlaylistPage {
  items: CuratedPlaylistItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export function PlaylistsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 0;
  const search = searchParams.get('search') ?? '';
  const publishedFilter = searchParams.get('published') ?? '';
  const selectedPublished = useMemo(
    () => publishedFilter ? publishedFilter.split(',').filter(Boolean) : [],
    [publishedFilter],
  );
  const [pageSize, setPageSize] = useState(20);

  const [searchInput, setSearchInput] = useState(search);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput); else next.delete('search');
        next.delete('page');
        return next;
      }, { replace: true });
    }, 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [searchInput, setSearchParams]);

  const setPage = useCallback((p: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (p > 0) next.set('page', String(p)); else next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setPublishedFilter = useCallback((value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set('published', value); else next.delete('published');
      next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const togglePublished = useCallback((value: string) => {
    const current = publishedFilter ? publishedFilter.split(',').filter(Boolean) : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setPublishedFilter(next.join(','));
  }, [publishedFilter, setPublishedFilter]);

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-playlists', page, pageSize, search, publishedFilter],
    queryFn: () =>
      apiClient.get<PlaylistPage>('/admin/curated-playlists', {
        page: page + 1,
        pageSize,
        ...(search ? { search } : {}),
        ...(publishedFilter ? { isPublished: publishedFilter } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const placeholderBg = useColorModeValue('gray.200', 'whiteAlpha.200');

  const columns = useMemo<ColumnDef<CuratedPlaylistItem, unknown>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ row }) => {
          const playlist = row.original;
          return (
            <Box
              w="40px"
              h="40px"
              borderRadius="lg"
              overflow="hidden"
              bg={placeholderBg}
              flexShrink={0}
            >
              {playlist.thumbnailUrl ? (
                <Image src={playlist.thumbnailUrl} alt="" w="100%" h="100%" objectFit="cover" />
              ) : null}
            </Box>
          );
        },
      },
      {
        accessorKey: 'name',
        header: t('admin.playlists.name'),
        cell: ({ getValue }) => (
          <Text color={textColor} fontWeight="600" fontSize="sm" noOfLines={1}>
            {getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'trackCount',
        header: t('admin.playlists.trackCount'),
        size: 100,
        cell: ({ getValue }) => (
          <Text color={secondaryColor} fontSize="sm">
            {getValue() as number}
          </Text>
        ),
      },
      {
        id: 'status',
        accessorKey: 'isPublished',
        header: t('admin.common.status'),
        size: 160,
        cell: ({ row }) => {
          const playlist = row.original;
          return (
            <Flex gap={1} flexWrap="wrap">
              <Badge
                colorScheme={playlist.isPublished ? 'green' : 'gray'}
                borderRadius="8px"
                px="10px"
                py="2px"
                fontSize="xs"
                fontWeight="600"
              >
                {playlist.isPublished
                  ? t('admin.playlists.published')
                  : t('admin.playlists.draft')}
              </Badge>
              {playlist.isLicensed && (
                <Badge
                  colorScheme="purple"
                  borderRadius="8px"
                  px="10px"
                  py="2px"
                  fontSize="xs"
                  fontWeight="600"
                  data-testid="playlist-licensed-badge"
                >
                  {t('admin.library.licensedBadge')}
                </Badge>
              )}
            </Flex>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: t('admin.common.createdAt', 'Created'),
        size: 140,
        cell: ({ getValue }) => (
          <Text color={secondaryColor} fontSize="sm">
            {new Date(getValue() as string).toLocaleDateString()}
          </Text>
        ),
      },
    ],
    [t, textColor, secondaryColor, placeholderBg],
  );

  const filterSections = useMemo<FilterSection[]>(() => [{
    key: 'published',
    label: t('admin.common.status'),
    options: [
      { value: 'true', label: t('admin.playlists.published') },
      { value: 'false', label: t('admin.playlists.draft') },
    ],
    selected: selectedPublished,
    onToggle: togglePublished,
  }], [t, selectedPublished, togglePublished]);

  const sidebarFilterCount = selectedPublished.length;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="playlists-list-page">
      <Flex gap={4}>
        {isSidebarOpen && (
          <FilterSidebar
            sections={filterSections}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            onClose={() => setIsSidebarOpen(false)}
            onClearAll={() => setPublishedFilter('')}
          />
        )}

        <Flex direction="column" flex={1}>
          <DataTable
            headerContent={
              <Flex align="center" gap={3} mb={4}>
                {!isSidebarOpen && (
                  <>
                    <Input
                      placeholder={t('admin.playlists.searchPlaceholder', 'Search playlists...')}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      flex={1}
                      minW={200}
                      size="md"
                      data-testid="playlists-search-input"
                      aria-label={t('common.search')}
                    />
                    <Button
                      leftIcon={<FilterIcon />}
                      variant="outline"
                      size="md"
                      borderRadius="10px"
                      onClick={() => setIsSidebarOpen(true)}
                      data-testid="filter-toggle-button"
                    >
                      {t('admin.library.filters.title', 'Filters')}
                      {sidebarFilterCount > 0 && (
                        <Badge ml={2} colorScheme="brand" borderRadius="full" fontSize="xs">
                          {sidebarFilterCount}
                        </Badge>
                      )}
                    </Button>
                  </>
                )}
                <Box ms={isSidebarOpen ? 'auto' : undefined}>
                  <Button
                    colorScheme="brand"
                    onClick={() => navigate('/curated-playlists/new')}
                    data-testid="btn-create-playlist"
                  >
                    {t('admin.playlists.create')}
                  </Button>
                </Box>
              </Flex>
            }
            data={data?.items ?? []}
            columns={columns}
            totalCount={data?.totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            isLoading={isLoading}
            onRowClick={(row) => navigate(`/curated-playlists/${row.id}`)}
            emptyText={t('admin.common.noResults')}
            data-testid="playlists-data-table"
          />
        </Flex>
      </Flex>
    </Box>
  );
}
